// Dependencies
import React, { useEffect, useState, useRef } from "react";
import clsx from "clsx";

// Material UI Components
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

// Components
import EvergreenFilterOption from "features/Evergreen/components/GenericLinksBar/EvergreenFilterOption/EvergreenFilterOption.component";
import LocationAutocomplete from "components/inputs/LocationAutocomplete/LocationAutocomplete.component";

// Helpers
import { inventoryRoute } from "routes";
import { getAddressComponents } from "helpers/google-helpers";
import { pluralizeString } from "helpers/string-format-helpers";
import { ISSERVER } from "helpers/server-helpers";

// Styles
import usePageWidth from "hooks/usePageWidth.hook";
import { useStyles } from "./GenericLinksBar.styles";

const GenericLinksBar = ({ navRefs, genericVehicle, specs }) => {
  const classes = useStyles();
  const [navbarClass, setNavbarClass] = useState(classes.navbar);
  const isSticky = navbarClass.includes("sticky");
  const [currentSection, setCurrentSection] = useState();

  const makeName = genericVehicle?.model?.make?.name || "";
  const modelName = genericVehicle?.model?.name;

  const [location, setLocation] = useState({});
  const [selectedMakeId, setSelectedMakeId] = useState();
  const [selectedModelId, setSelectedModelId] = useState();
  const [selectedYear, setSelectedYear] = useState();

  const { isWideDesktopWidth } = usePageWidth();

  const headerRefs = {
    scrollContainer: useRef(null),
    nearYou: useRef(null),
    allAbout: useRef(null),
    description: useRef(null),
    similar: useRef(null),
  };

  const maxYear = new Date().getFullYear() + 1;
  const yPos = useRef();
  const targetY = navRefs.navBar?.current?.getBoundingClientRect().top;
  const bodyY = !ISSERVER && document.body.getBoundingClientRect().top;

  const executeScroll = (scrollRef) => {
    scrollRef.current.scrollIntoView({ behavior: "smooth" });
  };

  const stickNavbar = () => {
    if (!window) {
      return;
    }

    if (window.scrollY > yPos.current) {
      setNavbarClass(classes.stickyHeader);
    } else {
      setNavbarClass(classes.navbar);
    }
  };

  const highlightCurrentSection = () => {
    if (!window) {
      return;
    }

    let closestSection = null;
    Object.keys(navRefs).forEach((key) => {
      if (key === "navBar") {
        return;
      }

      if (navRefs[key]?.current?.getBoundingClientRect().top <= 125) {
        closestSection = key;
      }
    });
    setCurrentSection(closestSection);
  };

  const handleScroll = () => {
    stickNavbar();
    highlightCurrentSection();
  };

  const handleLocation = (funcLocation) => {
    const { city, province } = getAddressComponents(
      funcLocation.address_components,
    );
    setLocation({ city, province });
  };

  const handleMakeChange = (event) => {
    setSelectedMakeId(event.target.value);
    setSelectedModelId(null);
  };
  const handleModelChange = (event) => {
    setSelectedModelId(event.target.value);
  };
  const handleYearChange = (event) => {
    setSelectedYear(event.target.value);
  };

  const handleUrl = () => {
    let url = inventoryRoute;
    if (selectedMakeId) {
      url = `${url}/${
        specs.makes.find((attributeMake) => attributeMake.id === selectedMakeId)
          .name
      }`;
      if (selectedModelId) {
        url = `${url}/${
          specs.models.find(
            (attributeModel) => attributeModel.id === selectedModelId,
          ).name
        }`;
      }
    }
    if (location?.province && location?.city) {
      url = `${url}/${location.province.toLowerCase()}/${location.city}`;
    }
    if (selectedYear) {
      url = `${url}?year=${selectedYear}-${selectedYear}`;
    }

    return url;
  };

  const getYearOptions = () =>
    Array.from({ length: 100 }, (_, i) => maxYear - i).map((year) => ({
      id: year,
      name: year,
    }));

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const makeId = specs.makes.find((attr) => attr.name === makeName)?.id;
    const modelId = specs.models.find(
      (attr) => attr.name === modelName && attr.makeId === makeId,
    )?.id;
    setSelectedMakeId(makeId);
    setSelectedModelId(modelId);
  }, [specs.makes]);

  useEffect(() => {
    yPos.current = targetY - bodyY;
  }, [yPos.current]);

  useEffect(() => {
    const container = headerRefs.scrollContainer.current;
    const target = headerRefs[currentSection]?.current;
    if (target && container) {
      container.scrollTo({ left: target.offsetLeft - 20, behavior: "smooth" });
    }
  }, [currentSection]);

  const getNavbar = () => {
    if (navbarClass === classes.navbar) {
      return null;
    }
    return (
      <div
        className={classes.pageLinks}
        ref={headerRefs.scrollContainer}
        data-component="GenericLinkBar"
      >
        <Button
          size="medium"
          variant="text"
          ref={headerRefs.nearYou}
          className={
            currentSection === "nearYou"
              ? clsx(classes.buttonLink, classes.highlightLink)
              : classes.buttonLink
          }
          onClick={() => {
            executeScroll(navRefs.nearYou);
          }}
        >
          {`${pluralizeString(makeName)} near you`}
        </Button>
        {/* <Button
          size="medium"
          variant="text"
          ref={headerRefs.allAbout}
          className={
            currentSection === 'allAbout' ? clsx(
              classes.buttonLink, classes.highlightLink
              ) : classes.buttonLink
          }
          onClick={() => {
            executeScroll(navRefs.allAbout);
          }}
        >
          {`${makeName} colour options`}
        </Button> */}
        <Button
          size="medium"
          variant="text"
          ref={headerRefs.description}
          className={
            currentSection === "description"
              ? clsx(classes.buttonLink, classes.highlightLink)
              : classes.buttonLink
          }
          onClick={() => {
            executeScroll(navRefs.description);
          }}
        >
          {`Is a ${makeName} right for you?`}
        </Button>
        <Button
          size="medium"
          variant="text"
          ref={headerRefs.similar}
          className={
            currentSection === "similar"
              ? clsx(classes.buttonLink, classes.highlightLink)
              : classes.buttonLink
          }
          onClick={() => {
            executeScroll(navRefs.similar);
          }}
        >
          Similar searches
        </Button>
      </div>
    );
  };

  const getSearchbar = () => (
    <div className={classes.headerSearchBar}>
      <EvergreenFilterOption
        className={classes.customSelect}
        label="Any Make"
        value={selectedMakeId ?? 0}
        name="make"
        options={specs.makes || []}
        onChange={handleMakeChange}
        data-cy="search-make"
      />
      <EvergreenFilterOption
        className={classes.customSelect}
        label="Any Model"
        value={selectedModelId ?? 0}
        name="model"
        options={
          specs.models
            ? specs.models.filter((model) => model.makeId === selectedMakeId)
            : []
        }
        onChange={handleModelChange}
        disabled={!selectedMakeId}
        data-cy="search-model"
      />
      <EvergreenFilterOption
        className={classes.customSelect}
        label="Any Year"
        value={selectedYear ?? 0}
        name="year"
        options={getYearOptions()}
        onChange={handleYearChange}
        data-cy="search-year"
        placeholder="Unset"
      />
      <LocationAutocomplete
        disableClearable
        size="small"
        name="Location"
        types={["(cities)"]}
        textFieldProps={{
          placeholder: "Location",
          className: classes.customAutocomplete,
        }}
        handleLocation={handleLocation}
      />
      <Button
        className={classes.searchButton}
        variant="contained"
        color="primary"
        fullWidth
        href={handleUrl()}
        data-cy="evergreen-search-button"
      >
        Search
      </Button>
    </div>
  );
  return (
    <div data-component="GenericLinksBar">
      <Typography
        variant="h2"
        className={classes.searchBarTitle}
        data-cy="vehicle-name"
      >
        Find other vehicles in your area
      </Typography>
      <div
        className={clsx(classes.flexContainer, navbarClass)}
        ref={navRefs.navBar}
        id="genericLinksBar"
      >
        {getNavbar()}
        {isWideDesktopWidth && isSticky && getSearchbar()}
      </div>
      {getSearchbar()}
    </div>
  );
};

export default GenericLinksBar;
